/**
 * The session layout menu: a small sliders-glyph button at the top of the
 * transcript column. Popover: Chat side Left|Right · Reset width. The
 * detail-panel row went with the node panel (DECISIONS #62); collapse and
 * the drag grip are the only other layout affordances.
 */

import { useEffect, useRef, useState } from 'react';

import type { Ulid } from '../../api/types';
import {
  TRANSCRIPT_DEFAULT_WIDTH,
  useProjectUi,
  useUiStore,
  type TranscriptSide,
} from '../../stores/uiStore';

function SlidersIcon() {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M4 7h10M18 7h2M4 12h4M12 12h8M4 17h12M20 17h0" />
      <circle cx="16" cy="7" r="2" />
      <circle cx="10" cy="12" r="2" />
      <circle cx="18" cy="17" r="2" />
    </svg>
  );
}


export function LayoutMenu({ productId }: { productId: Ulid }) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const projectUi = useProjectUi(productId);
  const setProjectUi = useUiStore((s) => s.setProjectUi);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (e: PointerEvent) => {
      if (rootRef.current !== null && !rootRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    window.addEventListener('pointerdown', onPointerDown);
    return () => window.removeEventListener('pointerdown', onPointerDown);
  }, [open]);

  const setSide = (side: TranscriptSide) => {
    setProjectUi(productId, { side });
  };

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        data-testid="layout-menu-button"
        title="Layout"
        aria-label="Layout options"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="flex h-6 w-6 cursor-pointer items-center justify-center rounded-chip border border-default bg-transparent text-tertiary hover:text-primary"
      >
        <SlidersIcon />
      </button>
      {open ? (
        <div
          data-testid="layout-menu"
          className="absolute top-7 right-0 z-50 w-52 rounded-card border border-default bg-raised p-2 shadow-overlay"
        >
          <div className="mb-1 text-[length:var(--text-micro-size)] font-medium tracking-[var(--text-micro-tracking)] uppercase text-tertiary">
            Chat side
          </div>
          <div
            role="group"
            aria-label="Chat side"
            className="mb-2 flex overflow-hidden rounded-card border border-default"
          >
            {(['left', 'right'] as const).map((side) => (
              <button
                key={side}
                type="button"
                data-testid={`layout-side-${side}`}
                onClick={() => setSide(side)}
                className={`h-6 flex-1 cursor-pointer border-none text-[length:var(--text-micro-size)] capitalize transition-colors duration-(--motion-hover) ${
                  projectUi.side === side
                    ? 'bg-accent-subtle text-(--accent)'
                    : 'bg-transparent text-tertiary hover:text-primary'
                }`}
              >
                {side}
              </button>
            ))}
          </div>
          <button
            type="button"
            data-testid="layout-reset-width"
            onClick={() => setProjectUi(productId, { transcriptWidth: TRANSCRIPT_DEFAULT_WIDTH })}
            className="h-6 w-full cursor-pointer rounded-chip border border-strong bg-transparent px-2 text-left text-[length:var(--text-micro-size)] text-secondary hover:text-primary"
          >
            Reset width
          </button>
        </div>
      ) : null}
    </div>
  );
}
